// Deduped by productCode; salesAmount is per SKU. Only range moves the total, not facings.
import { Pog } from "@CommonTypes/merchflow/pog/pog";

export const respondsToFacings = false;

const TOP_CONTRIBUTOR_COUNT = 5;

export interface ExpectedSalesContributor {
  productCode: string;
  name: string;
  salesAmount: number;
}

export interface ExpectedSales {
  total: number;
  skuCount: number;
  placementCount: number;
  topContributors: ExpectedSalesContributor[];
  unrangedTotal: number;
  /** Identical sales on every SKU is placeholder data; callers should hide the total. */
  isPlaceholderData: boolean;
}

const sumByProductCode = (
  products: readonly { productCode: string; salesAmount: number }[],
  seen: Set<string>,
) => {
  let total = 0;
  for (const product of products) {
    if (seen.has(product.productCode)) continue;
    seen.add(product.productCode);
    total += product.salesAmount || 0;
  }
  return total;
};

export const pogExpectedSales = (pog: Pog | null): ExpectedSales | null => {
  if (!pog) return null;
  try {
    const placed = pog.planogram.getAllPlanogramProducts();

    const counted = new Map<string, ExpectedSalesContributor>();
    for (const product of placed) {
      if (counted.has(product.productCode)) continue;
      counted.set(product.productCode, {
        productCode: product.productCode,
        name: product.name,
        salesAmount: product.salesAmount || 0,
      });
    }

    const contributors = [...counted.values()];
    const total = contributors.reduce((sum, item) => sum + item.salesAmount, 0);

    const unrangedTotal = sumByProductCode(pog.unrangedItems, new Set<string>());

    const first = contributors[0]?.salesAmount;
    const isPlaceholderData =
      contributors.length > 1 && contributors.every((item) => item.salesAmount === first);

    return {
      total,
      skuCount: contributors.length,
      placementCount: placed.length,
      topContributors: [...contributors]
        .sort((a, b) => b.salesAmount - a.salesAmount)
        .slice(0, TOP_CONTRIBUTOR_COUNT),
      unrangedTotal,
      isPlaceholderData,
    };
  } catch {
    return null;
  }
};
